/**
 * DialerOrchestrationContext — Refs late-bound transversales.
 *
 * 2026-06-04 (Fowler etape 3/5 du decoupage) — Resout le SEUL vrai couplage
 * transversal documente dans la carte des dependances §4.3.
 *
 * Probleme adresse :
 *   TwilioDeviceContext doit pouvoir lire `powerMode` (settings),
 *   `dialerRunState` (session) et appeler `handleStopAll` (actions) DEPUIS
 *   un callback `onExternalIncomingRinging` cree au mount du device. Sans
 *   refs late-bound, le callback capture les valeurs initiales (undefined,
 *   'idle', null) et ne reagit pas aux changements ulterieurs.
 *
 * Solution :
 *   Un context minimal qui expose 3 refs. Les ecrivains (settings, session,
 *   actions) appellent un setter qui synchronise la ref. Les lecteurs
 *   (TwilioDeviceContext) lisent `ref.current`.
 *
 * Responsabilite unique :
 *   "Permet aux callbacks low-level Twilio de lire l'etat metier le plus
 *    recent sans capture stale."
 *
 * Pas de cycle : Orchestration est un mailbox passif — il ne lit personne.
 */

import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';

// ── Public shape ──────────────────────────────────────────────────────────────

type StopAllFn = () => void;

export interface DialerOrchestrationContextValue {
  // Lecteurs : appelent .current dans leurs callbacks late-bound.
  powerModeRef: React.MutableRefObject<boolean>;
  dialerRunStateRef: React.MutableRefObject<string>;
  handleStopAllRef: React.MutableRefObject<StopAllFn | null>;

  // Ecrivains : synchronisent la ref depuis un useEffect.
  syncPowerMode: (value: boolean) => void;
  syncDialerRunState: (value: string) => void;
  syncHandleStopAll: (fn: StopAllFn | null) => void;
}

const DialerOrchestrationContext =
  createContext<DialerOrchestrationContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function DialerOrchestrationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const powerModeRef = useRef<boolean>(false);
  const dialerRunStateRef = useRef<string>('idle');
  const handleStopAllRef = useRef<StopAllFn | null>(null);

  const syncPowerMode = useCallback((value: boolean) => {
    powerModeRef.current = value;
  }, []);
  const syncDialerRunState = useCallback((value: string) => {
    dialerRunStateRef.current = value;
  }, []);
  const syncHandleStopAll = useCallback((fn: StopAllFn | null) => {
    handleStopAllRef.current = fn;
  }, []);

  const value = useMemo<DialerOrchestrationContextValue>(
    () => ({
      powerModeRef,
      dialerRunStateRef,
      handleStopAllRef,
      syncPowerMode,
      syncDialerRunState,
      syncHandleStopAll,
    }),
    [syncPowerMode, syncDialerRunState, syncHandleStopAll],
  );

  return (
    <DialerOrchestrationContext.Provider value={value}>
      {children}
    </DialerOrchestrationContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useDialerOrchestration(): DialerOrchestrationContextValue {
  const ctx = useContext(DialerOrchestrationContext);
  if (!ctx) {
    throw new Error(
      'useDialerOrchestration must be used within <DialerOrchestrationProvider>',
    );
  }
  return ctx;
}
